// src/admin/components/BrandManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  Pagination,
  InputAdornment,
  Tooltip,
  useTheme,
  useMediaQuery,
  CardMedia,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelCircleIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Store as StoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import AppsIcon from '@mui/icons-material/Apps';
import { useAuth } from '../../../contexts/AuthContext';
import { Brand, MAX_FEATURED_BRANDS } from './brandService';
import { useBrandsPaginated, useBrandStats, useBrandMutations } from '../../../hooks/useBrands';
import ToastNotification from '../common/Toastnotification';

const BRANDS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ── Chip style helper ─────────────────────────────────────────────────────────
const getSoftChipSx = (color: string) => {
  const palette: Record<string, { bg: string; border: string; text: string; iconColor: string }> = {
    warning: {
      bg: 'rgba(255, 167, 38, 0.12)',
      border: 'rgba(255, 167, 38, 0.4)',
      text: '#b76e00',
      iconColor: '#e68900',
    },
    info: {
      bg: 'rgba(41, 182, 246, 0.12)',
      border: 'rgba(41, 182, 246, 0.4)',
      text: '#0277bd',
      iconColor: '#039be5',
    },
    primary: {
      bg: 'rgba(33, 150, 243, 0.12)',
      border: 'rgba(33, 150, 243, 0.4)',
      text: '#1565c0',
      iconColor: '#1976d2',
    },
    success: {
      bg: 'rgba(102, 187, 106, 0.15)',
      border: 'rgba(102, 187, 106, 0.45)',
      text: '#2e7d32',
      iconColor: '#43a047',
    },
    error: {
      bg: 'rgba(239, 83, 80, 0.12)',
      border: 'rgba(239, 83, 80, 0.4)',
      text: '#c62828',
      iconColor: '#e53935',
    },
    secondary: {
      bg: 'rgba(171, 71, 188, 0.10)',
      border: 'rgba(171, 71, 188, 0.35)',
      text: '#6a1b9a',
      iconColor: '#8e24aa',
    },
    inactive: {
      bg: 'rgba(97, 97, 97, 0.14)',
      border: 'rgba(97, 97, 97, 0.50)',
      text: 'text.secondary',
      iconColor: 'text.secondary',
    },
    default: {
      bg: 'rgba(158, 158, 158, 0.12)',
      border: 'rgba(158, 158, 158, 0.4)',
      text: '#424242',
      iconColor: '#757575',
    },
  };
  const p = palette[color] ?? palette.default;
  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ── LoadingOverlay ────────────────────────────────────────────────────────────
interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg =
    theme.palette.mode === 'dark'
      ? 'rgba(26, 29, 36, 0.85)'
      : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading brands...
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BrandManagement
// ─────────────────────────────────────────────────────────────────────────────
const BrandManagement: React.FC = () => {
  // CHANGE: destructure hasPermission + isSuperAdmin
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();

  // ── Permission flags ───────────────────────────────────────────────────────
  const canView = isSuperAdmin || hasPermission('brand.view');
  const canCreate = isSuperAdmin || hasPermission('brand.create');
  const canEdit = isSuperAdmin || hasPermission('brand.edit');
  const canDelete = isSuperAdmin || hasPermission('brand.delete');
  // Toggle active status and toggle featured both map to brand.edit
  // since they are inline field updates, not separate feature codes
  const canToggleStatus = isSuperAdmin || hasPermission('brand.edit');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // ── FIX: imageRemovedInEdit tracks that the admin clicked "Remove Image"
  // during an edit session. NO backend call is made at that point.
  // The actual backend removal (Storage delete + Firestore clear) is deferred
  // to handleSubmit, where it only executes if the user also provides a
  // replacement image and then clicks Save. If the user cancels or closes the
  // drawer, the original image on the backend remains untouched.
  const [imageRemovedInEdit, setImageRemovedInEdit] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    websiteUrl: '',
    isActive: true,
    featured: false,
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const [deleteTarget, setDeleteTarget] = useState<Brand | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: brandsData,
    isLoading: brandsLoading,
    isFetching: brandsIsFetching,
  } = useBrandsPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    pageSize: BRANDS_PER_PAGE,
  });

  const { data: statsData } = useBrandStats();

  const { create, update, removeImage, remove } = useBrandMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const brands = brandsData?.brands ?? [];
  const totalBrands = brandsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalBrands / BRANDS_PER_PAGE);
  const stats = statsData ?? { total: 0, active: 0, inactive: 0, featured: 0 };
  const loading = brandsLoading || brandsIsFetching;
  const saveAction = drawerMode === 'add' ? 'Creating Brand…' : 'Updating Brand…';

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => setCurrentPage(1);

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) =>
    setCurrentPage(page);

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Status filter ─────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  // ── Clear filters ─────────────────────────────────────────────────────────
  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Image helpers ─────────────────────────────────────────────────────────
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size should be less than 5MB', 'error');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
      // If the user picks a new file after having flagged the old one for
      // removal, keep imageRemovedInEdit = true so the save path knows to
      // delete the old backend image when it uploads the new one.
      // (No change needed here — the flag stays true and handleSubmit uses it.)
    }
  };

  // ── FIX: handleRemoveImage only clears LOCAL state — no backend call.
  // The original image on the backend is preserved until the user either:
  //   (a) saves with a new replacement image  → old image deleted inside updateBrand
  //   (b) cancels / closes the drawer         → nothing changed on backend
  // This prevents the data-loss scenario where the admin removes the image
  // preview and then closes the drawer, leaving the brand with no image.
  const handleRemoveImage = () => {
    setImagePreview('');
    setImageFile(null);
    if (drawerMode === 'edit') {
      // Flag that the persisted image should be replaced on save.
      // The actual backend deletion is deferred to handleSubmit.
      setImageRemovedInEdit(true);
    }
  };

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ name: '', description: '', websiteUrl: '', isActive: true, featured: false });
    setSelectedBrand(null);
    setImageFile(null);
    setImagePreview('');
    setImageRemovedInEdit(false);
  };

  const handleAddBrand = () => {
    if (!canCreate) return; // ADD
    resetForm();
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const handleEditBrand = (brand: Brand) => {
    if (!canEdit) return;
    setFormData({
      name: brand.name,
      description: brand.description,
      websiteUrl: brand.websiteUrl || '',
      isActive: brand.isActive,
      featured: brand.featured,
    });
    setSelectedBrand(brand);
    setImagePreview(brand.imageUrl);
    setImageFile(null);
    setImageRemovedInEdit(false);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    // Closing / cancelling resets everything — the backend image was never
    // touched (because handleRemoveImage no longer calls the backend),
    // so no cleanup is needed here.
    setDrawerOpen(false);
    resetForm();
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteBrand = (brand: Brand) => { if (!canEdit) return; setDeleteTarget(brand); }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      const result = await remove.mutateAsync(deleteTarget.id!);
      if (result.success) {
        setDeleteTarget(null);
        resetToFirstPage();
        showToast('Brand deleted successfully', 'success');
      } else {
        showToast(result.message || 'Failed to delete brand', 'error');
      }
    } catch {
      showToast('Failed to delete brand', 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Toggle status ──────────────────────────────────────────────────────────
  const handleToggleStatus = async (brand: Brand) => {
    if (!canEdit) return;
    try {
      const newStatus = !brand.isActive;
      const result = await update.mutateAsync({
        brandId: brand.id!,
        brandData: {
          name: brand.name,
          description: brand.description,
          websiteUrl: brand.websiteUrl,
          isActive: newStatus,
          featured: brand.featured,
        },
      });
      if (result.success) {
        resetToFirstPage();
        showToast(`Brand ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      } else {
        showToast(result.message || 'Failed to update brand status', 'error');
      }
    } catch {
      showToast('Failed to update brand status', 'error');
    }
  };

  const handleToggleFeatured = async (brand: Brand) => {
    if (!canEdit) return;
    try {
      const newFeaturedStatus = !brand.featured;

      if (newFeaturedStatus && stats.featured >= MAX_FEATURED_BRANDS) {
        showToast(
          `Maximum ${MAX_FEATURED_BRANDS} brands can be featured. Please unfeature a brand first.`,
          'warning'
        );
        return;
      }

      const result = await update.mutateAsync({
        brandId: brand.id!,
        brandData: {
          name: brand.name,
          description: brand.description,
          websiteUrl: brand.websiteUrl,
          isActive: brand.isActive,
          featured: newFeaturedStatus,
        },
      });
      if (result.success) {
        resetToFirstPage();
        showToast(
          newFeaturedStatus ? 'Brand marked as featured' : 'Brand removed from featured',
          'success'
        );
      } else {
        showToast(result.message || 'Failed to update featured status', 'error');
      }
    } catch {
      showToast('Failed to update featured status', 'error');
    }
  };

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      showToast('You must be logged in to perform this action', 'error');
      return;
    }
    if (!formData.name.trim()) { showToast('Please enter a brand name', 'error'); return; }
    if (!formData.description.trim()) { showToast('Please enter a description', 'error'); return; }

    // Add mode: image is always required.
    if (!selectedBrand && !imageFile) {
      showToast('Please select an image', 'error');
      return;
    }

    // Edit mode: if the admin removed the existing image they MUST supply a
    // replacement before saving. Without this guard the brand would be saved
    // with no image at all.
    if (selectedBrand && imageRemovedInEdit && !imageFile) {
      showToast('Please upload a replacement image before saving', 'error');
      return;
    }

    // Guard: block if trying to create/update with featured=true and limit reached
    if (formData.featured) {
      const isAlreadyFeatured = selectedBrand?.featured ?? false;
      if (!isAlreadyFeatured && stats.featured >= MAX_FEATURED_BRANDS) {
        showToast(
          `Maximum ${MAX_FEATURED_BRANDS} brands can be featured. Please unfeature a brand first.`,
          'warning'
        );
        return;
      }
    }

    setSaveLoading(true);
    try {
      let result;
      if (selectedBrand) {
        // ── FIX: If the admin removed the old image AND supplied a new file,
        // we need to also delete the old image from Storage + clear Firestore.
        // updateBrand already handles replacing the image when imageFile is
        // provided (it uploads the new file and deletes the old one).
        // However, if imageRemovedInEdit is true but there was already no
        // persisted image URL (edge case), updateBrand handles that gracefully
        // because deleteBrandImage is a no-op when imageUrl is empty.
        //
        // The key insight: by passing imageFile here, updateBrand will:
        //   1. Upload the new image to Storage
        //   2. Write the new imageUrl to Firestore
        //   3. Delete the old image from Storage (via the existing logic in updateBrand)
        // So no separate removeImage mutation call is needed at all.
        result = await update.mutateAsync({
          brandId: selectedBrand.id!,
          brandData: {
            name: formData.name,
            description: formData.description,
            websiteUrl: formData.websiteUrl,
            isActive: formData.isActive,
            featured: formData.featured,
          },
          imageFile: imageFile || undefined,
        });
        if (result.success) showToast('Brand updated successfully', 'success');
        else showToast(result.message || 'Failed to update brand', 'error');
      } else {
        result = await create.mutateAsync({
          brandData: {
            name: formData.name,
            description: formData.description,
            websiteUrl: formData.websiteUrl,
            isActive: formData.isActive,
            featured: formData.featured,
            createdBy: '',
          },
          imageFile: imageFile!,
          userId: currentUser.uid,
        });
        if (result.success) showToast('Brand created successfully', 'success');
        else showToast(result.message || 'Failed to create brand', 'error');
      }
      if (result.success) {
        setDrawerOpen(false);
        resetForm();
        resetToFirstPage();
      }
    } catch {
      showToast('Failed to save brand', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <StoreIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              You don't have permission to view brands. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && brands.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading brands...</Typography>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      ref={containerRef}
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AppsIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Brand Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your brand partners and featured brands
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
            <Button onClick={handleAddBrand} variant="contained" startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
              Add New Brand
            </Button>
          )}
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Total Brands
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.total}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{stats.active} Active</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>{stats.inactive} Inactive</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Active</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.active}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.active / stats.total) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Inactive</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.inactive}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.inactive / stats.total) * 100).toFixed(1)}% suspended` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Featured (max {MAX_FEATURED_BRANDS})
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.featured} / {MAX_FEATURED_BRANDS}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {MAX_FEATURED_BRANDS - stats.featured} slot{MAX_FEATURED_BRANDS - stats.featured !== 1 ? 's' : ''} remaining
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by name, description... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>Clear</Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="inactive">Inactive Only</MenuItem>
                      <MenuItem value="featured">Featured Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${brands.length} brands matching your filters`
                        : `Showing ${brands.length} brands on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>

          {searchQuery && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Found {brands.length} brands matching "{searchQuery}"
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

        {brands.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <StoreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Brands Match Your Filters' : 'No Brands Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : "You haven't created any brands yet. Start by adding your first brand."}
                </Typography>
                {hasActiveFilters ? (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
                ) : (
                  canCreate && ( // ADD: wrap
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddBrand}>Add New Brand</Button>
                  )
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {brands.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 600, md: 800 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40px"></TableCell>
                    <TableCell width="80px">Logo</TableCell>
                    <TableCell>Brand Name</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Website</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created At</TableCell>
                    {(canEdit || canToggleStatus || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brands.map((brand) => (
                    <TableRow
                      key={brand.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      {/* Featured star */}
                      <TableCell>
                        <Tooltip
                          title={
                            brand.featured
                              ? 'Remove from featured'
                              : stats.featured >= MAX_FEATURED_BRANDS
                                ? `Max ${MAX_FEATURED_BRANDS} featured brands reached`
                                : 'Mark as featured'
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => handleToggleFeatured(brand)}
                              size="small"
                              disabled={!brand.featured && stats.featured >= MAX_FEATURED_BRANDS}
                              sx={{
                                color: brand.featured ? '#ff9800' : 'inherit',
                                '&:hover': { color: brand.featured ? '#f57c00' : '#ff9800' },
                                '&.Mui-disabled': { opacity: 0.4 },
                              }}
                            >
                              {brand.featured
                                ? <StarIcon fontSize="small" />
                                : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* Logo */}
                      <TableCell>
                        <Box
                          sx={{
                            width: { xs: 44, sm: 56 },
                            height: { xs: 44, sm: 56 },
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            bgcolor: 'grey.100',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {brand.imageUrl ? (
                            <Box
                              component="img"
                              src={brand.imageUrl}
                              alt={brand.name}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          ) : (
                            <StoreIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
                          )}
                        </Box>
                      </TableCell>

                      {/* Brand name + Featured chip */}
                      <TableCell>
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.5}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                            {brand.name}
                          </Typography>
                          {brand.featured && (
                            <Chip
                              label="Featured"
                              size="small"
                              variant="outlined"
                              sx={{ ...getSoftChipSx('warning'), height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Stack>
                        {brand.websiteUrl && (
                          <Typography variant="caption" color="text.secondary" noWrap
                            sx={{ display: { xs: 'block', sm: 'none' }, maxWidth: 140 }}>
                            {brand.websiteUrl.replace(/^https?:\/\//, '')}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ maxWidth: 260 }}>
                          {brand.description.length > 100
                            ? `${brand.description.substring(0, 100)}...`
                            : brand.description}
                        </Typography>
                      </TableCell>

                      {/* Status chip */}
                      <TableCell>
                        <Chip
                          label={brand.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          variant="outlined"
                          icon={
                            brand.isActive
                              ? <CheckCircleIcon fontSize="small" />
                              : <CancelCircleIcon fontSize="small" />
                          }
                          sx={getSoftChipSx(brand.isActive ? 'success' : 'inactive')}
                        />
                      </TableCell>

                      {/* Website */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {brand.websiteUrl ? (
                          <Tooltip title={brand.websiteUrl}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <LanguageIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                                {brand.websiteUrl.replace(/^https?:\/\//, '')}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>

                      {/* Created At */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {brand.createdAt
                            ? new Intl.DateTimeFormat('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            }).format(
                              brand.createdAt instanceof Date
                                ? brand.createdAt
                                : (brand.createdAt as any).toDate()
                            )
                            : '-'}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      {(canEdit || canToggleStatus || canDelete) && (  // ADD wrap
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">

                            {canEdit && (  // ADD wrap
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditBrand(brand)} size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {canToggleStatus && (  // ADD wrap
                              <Tooltip title={brand.isActive ? 'Deactivate' : 'Activate'}>
                                <IconButton onClick={() => handleToggleStatus(brand)} size="small"
                                  color={brand.isActive ? 'warning' : 'success'}>
                                  {brand.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}

                            {canDelete && (  // ADD wrap
                              <Tooltip title="Delete">
                                <IconButton onClick={() => handleDeleteBrand(brand)}
                                  disabled={deleteLoading === brand.id} size="small" color="error">
                                  {deleteLoading === brand.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}

                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalBrands === 0 ? 0 : (currentPage - 1) * BRANDS_PER_PAGE + 1}–
                    {Math.min(currentPage * BRANDS_PER_PAGE, totalBrands)} of {totalBrands} brands
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Add / Edit Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            px: 3, py: 2, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <Typography variant="h6">
              {drawerMode === 'add' ? 'Add New Brand' : 'Edit Brand'}
            </Typography>
            <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <Box sx={{
              flex: 1, overflow: 'auto', px: 3, py: 3,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
            }}>
              <Grid container spacing={3}>
                {/* Name */}
                <Grid item xs={12}>
                  <TextField fullWidth label="Brand Name" name="name" value={formData.name}
                    onChange={handleInputChange} required placeholder="Enter brand name" />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField fullWidth label="Description" name="description" value={formData.description}
                    onChange={handleInputChange} required multiline rows={4}
                    placeholder="Enter brand description" />
                </Grid>

                {/* Website */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Website URL (Optional)" name="websiteUrl"
                    value={formData.websiteUrl} onChange={handleInputChange}
                    type="url" placeholder="https://example.com"
                    InputProps={{
                      startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                  />
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                {/* ── Image upload ──────────────────────────────────────────────────
                    BEHAVIOUR SUMMARY
                    • Add mode   : image required; upload button shown until file chosen.
                    • Edit mode  : existing image shown.
                      – "Remove Image" clears the LOCAL preview only (no backend call).
                        imageRemovedInEdit is set to true so the save path knows the
                        old image must be replaced.
                      – Saving with imageRemovedInEdit=true but no new file is blocked
                        by the validation in handleSubmit.
                      – Once a replacement file is chosen, saving will upload the new
                        image and delete the old one (handled inside updateBrand).
                      – Cancelling / closing the drawer resets everything — the
                        original backend image is untouched.
                ──────────────────────────────────────────────────────────────────── */}
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">Brand Image</Typography>
                    {/* Show required asterisk in add mode, or in edit mode when
                        the image has been removed and awaits a replacement. */}
                    {(drawerMode === 'add' || imageRemovedInEdit) && (
                      <Typography variant="subtitle2" color="error">*</Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    {drawerMode === 'edit'
                      ? imageRemovedInEdit && !imageFile
                        ? 'Image removed — please upload a replacement before saving'
                        : 'Upload a new image to replace the current one (max 5MB)'
                      : 'Upload a brand logo (max 5MB, recommended: 800×800 px)'}
                  </Typography>
                  <Box
                    sx={{
                      border: '1px dashed',
                      // Highlight the border in error colour when image was removed
                      // but no replacement has been chosen yet, to signal the user.
                      borderColor: imageRemovedInEdit && !imageFile ? 'error.main' : 'divider',
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    {imagePreview ? (
                      <Box>
                        {/* Preview — objectFit: contain keeps full logo visible */}
                        <Box
                          component="img"
                          src={imagePreview}
                          alt="Brand preview"
                          sx={{
                            width: '100%',
                            maxHeight: 200,
                            objectFit: 'contain',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: 'grey.100',
                            display: 'block',
                          }}
                        />
                        <Button
                          onClick={handleRemoveImage}
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          fullWidth
                        >
                          Remove Image
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <Button
                          component="label"
                          variant="outlined"
                          // Use error colour when removal flag is set and no file chosen
                          color={imageRemovedInEdit && !imageFile ? 'error' : 'primary'}
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                        >
                          {imageRemovedInEdit && !imageFile
                            ? 'Upload Replacement Image'
                            : 'Upload Brand Image'}
                          <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                        </Button>
                        {/* Inline helper text when in error state */}
                        {imageRemovedInEdit && !imageFile && (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                            sx={{ mt: 1, textAlign: 'center' }}
                          >
                            A replacement image is required to save
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                {/* Active / Featured toggles */}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch name="isActive" checked={formData.isActive}
                        onChange={handleInputChange} color="success" />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Tooltip
                    title={
                      formData.featured
                        ? ''
                        : stats.featured >= MAX_FEATURED_BRANDS
                          ? `Maximum ${MAX_FEATURED_BRANDS} featured brands reached`
                          : ''
                    }
                    disableHoverListener={formData.featured || stats.featured < MAX_FEATURED_BRANDS}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          name="featured"
                          checked={formData.featured}
                          onChange={handleInputChange}
                          color="warning"
                          disabled={!formData.featured && stats.featured >= MAX_FEATURED_BRANDS}
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>Featured</span>
                          {stats.featured >= MAX_FEATURED_BRANDS && !formData.featured && (
                            <Typography variant="caption" color="error">
                              (limit reached)
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>

            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<CancelIcon />}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {drawerMode === 'add' ? 'Create Brand' : 'Update Brand'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Brand</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{deleteTarget?.name}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay ───────────────────────────────────── */}
      <Backdrop
        open={saveLoading || deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {deleteDialogLoading ? 'Deleting Brand…' : saveAction}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default BrandManagement;